import {getFormResultAnswers, getFormResultConfig, getFormResultDate} from "./formResultHelpers.js";

const hasValue = value => value !== null && value !== undefined && value !== "";

export function getAnchoredMeanConfigErrors(config = {}) {
	if (!config.anchoredMean) return [];
	const fields = config.fields || [];
	const ratingIds = config.anchoredMean.ratingFieldIds || [];
	const min = Number(config.anchoredMean.min);
	const max = Number(config.ratingScaleMax);
	if (!Array.isArray(ratingIds) || !Number.isInteger(min) || ![0, 1].includes(min) || !Number.isInteger(max) || max < 3 || max > 10) return ["Invalid anchored rating scale"];
	if (new Set(ratingIds).size !== ratingIds.length || ratingIds.some(id => !fields.some(field => field.id === id && field.type === "SCALE"))) return ["Scoring refers to a removed or repeated rating"];
	for (const field of fields.filter(item => ratingIds.includes(item.id))) {
		if (!Array.isArray(field.anchors) || field.anchors.length !== max - min + 1 || field.anchors.some(anchor => !String(anchor?.title || "").trim())) return ["Each rating needs an anchor for every configured value"];
		if (field.anchors.some((anchor, index) => Number(anchor.value) !== min + index)) return ["Rating anchors must match the configured scale"];
		const options = field.nonratingOptions || [];
		if (!Array.isArray(options) || options.some(option => !option.id || !String(option.title || "").trim()) || new Set(options.map(option => option.id)).size !== options.length) return ["Invalid nonrating choices"];
	}
	return [];
}

export function getAnchoredMeanPayloadErrors(payload = {}, config = {}) {
	if (!config.anchoredMean) return [];
	const answers = payload.answers || {};
	const min = Number(config.anchoredMean.min);
	const max = Number(config.ratingScaleMax);
	for (const field of (config.fields || []).filter(item => item.type === "SCALE")) {
		const value = answers[field.id];
		if (!hasValue(value)) continue;
		if ([...(field.nonratingOptions || []), ...(field.archivedNonratingOptions || [])].some(option => option.id === value)) continue;
		if (typeof value !== "number" || !Number.isInteger(value) || value < min || value > max) return ["Rating is outside the configured scale"];
	}
	return [];
}

export function getAnchoredMeanReport(config = {}, answers = {}) {
	const fields = config.fields || [];
	const ratingIds = config.anchoredMean?.ratingFieldIds || [];
	const required = ratingIds.map(id => fields.find(field => field.id === id)).filter(field => field?.isCompletionRequired);
	const missing = required.filter(field => !Number.isInteger(answers[field.id]) || answers[field.id] < Number(config.anchoredMean.min) || answers[field.id] > Number(config.ratingScaleMax));
	const numeric = required.map(field => answers[field.id]);
	const mean = required.length && !missing.length ? numeric.reduce((sum, value) => sum + value, 0) / required.length : null;
	return {mean, missingFieldIds: missing.map(field => field.id), ratingFieldIds: ratingIds};
}

export function getAnchoredMeanResultView(config, result = {}, priorResults = []) {
	const savedConfig = getFormResultConfig(undefined, result);
	const current = Object.keys(savedConfig).length ? savedConfig : config || {};
	const answers = getFormResultAnswers(result);
	const report = getAnchoredMeanReport(current, answers);
	const history = priorResults.filter(prior => prior.toolType === result.toolType && (!result.id || prior.id !== result.id)).map((prior, index) => {
		const priorSavedConfig = getFormResultConfig(undefined, prior);
		const priorConfig = Object.keys(priorSavedConfig).length ? priorSavedConfig : current;
		const priorAnswers = getFormResultAnswers(prior);
		const priorReport = getAnchoredMeanReport(priorConfig, priorAnswers);
		return {id: prior.id || index, config: priorConfig, date: priorAnswers.sessionDate || getFormResultDate(prior), homeworkStart: priorAnswers.homeworkStart || "", homeworkEnd: priorAnswers.homeworkEnd || "", answers: priorAnswers, report: priorReport};
	}).sort((a, b) => a.date.localeCompare(b.date));
	const complete = [...history, {id: result.id || "current", date: answers.sessionDate || getFormResultDate(result), report}].filter(row => row.report.mean !== null);
	return {config: current, answers, report, history, treatmentMean: complete.length ? complete.reduce((sum, row) => sum + row.report.mean, 0) / complete.length : null};
}
